import { Theme } from "@material-ui/core";
import type { Place, ViewState } from "../types/warehouse.types";
import { DirtyTracker } from "./core/DirtyTracker";
import { DirtyFlag, PlaceRendererConfig } from "./core/types";
import { PlaceStyleManager } from "./place/PlaceStyleManager";
import { PlaceLabelRenderer } from "./place/PlaceLabelRenderer";
import { PlaceLayoutCache } from "./place/PlaceLayoutCache";
import { PlaceSpriteFactory } from "./place/PlaceSpriteFactory";
import { ColumnBuilder } from "./place/ColumnBuilder";
import { ColumnSorter } from "./place/ColumnSorter";
import { Culler } from "./place/Culler";
import { PlaceDrawer } from "./place/PlaceDrawer";
import { RenderPipeline, RenderContext } from "./place/RenderPipeline";
import { BaseRenderer } from "./base/BaseRenderer";

// Re-export for backward compatibility
export type { PlaceRendererConfig, PlaceColumn } from "./core/types";

/**
 * Render statistics type
 */
type RenderStats = {
  frameCount: number;
  totalTime: number;
  avgTime: number;
  lastFrameTime: number;
};

/**
 * Main PlaceRenderer using modular architecture
 *
 * Architecture:
 * - Facade Pattern: Provides simple interface to complex subsystems
 * - Strategy Pattern: Pluggable rendering strategies
 * - Pipeline Pattern: Configurable render phases
 * - Dirty Tracking: Optimized updates
 *
 * Subsystems:
 * - StyleManager: Handles colors and styles
 * - LabelRenderer: Renders labels with LOD
 * - LayoutCache: Caches geometric calculations
 * - SpriteFactory: Creates and caches sprites
 * - ColumnBuilder: Groups places into columns
 * - ColumnSorter: Sorts for painter's algorithm
 * - Culler: Viewport culling
 * - PlaceDrawer: Actual drawing
 * - RenderPipeline: Orchestrates rendering phases
 */
export class PlaceRenderer extends BaseRenderer<PlaceRendererConfig> {
  // --- Subsystems (Facade Pattern) ---
  private styleManager: PlaceStyleManager;
  private labelRenderer: PlaceLabelRenderer;
  private layoutCache: PlaceLayoutCache;
  private spriteFactory: PlaceSpriteFactory;
  private columnBuilder: ColumnBuilder;
  private columnSorter: ColumnSorter;
  private culler: Culler;
  private placeDrawer: PlaceDrawer;

  // --- Orchestration ---
  private pipeline: RenderPipeline;
  private dirtyTracker: DirtyTracker;

  // --- Performance tracking ---
  private renderStats: RenderStats = {
    frameCount: 0,
    totalTime: 0,
    avgTime: 0,
    lastFrameTime: 0,
  };

  constructor(theme: Theme, config?: Partial<PlaceRendererConfig>) {
    super(theme, {
      selectionLineWidth: 2,
      borderLineWidth: 1,
      depthFactorPerLevel: 0.05,
      isoDepthFactorX: 0.4,
      isoDepthFactorY: -0.2,
      spacingFactor: 0.1,
      labelFont: (scale: number) =>
        `bold ${Math.min(scale * 10, 10)}px ${this.theme.typography.fontFamily}`,
      labelMinScale: 0.5,
      labelMinTextSize: 3,
      addressLabelFont: (scale: number) =>
        `bold ${Math.max(8, 10 * scale)}px ${this.theme.typography.fontFamily}`,
      addressLabelMinScale: 1,
      addressLabelOffset: 20,
    }, config);

    // Initialize subsystems
    this.styleManager = new PlaceStyleManager(this.theme, this.config);
    this.labelRenderer = new PlaceLabelRenderer(this.theme, this.config);
    this.layoutCache = new PlaceLayoutCache();
    this.spriteFactory = new PlaceSpriteFactory();
    this.columnBuilder = new ColumnBuilder();
    this.columnSorter = new ColumnSorter();
    this.culler = new Culler();

    this.placeDrawer = new PlaceDrawer(
      this.config,
      this.layoutCache,
      this.spriteFactory,
      this.styleManager,
      this.labelRenderer
    );

    // Initialize orchestration
    this.pipeline = new RenderPipeline();
    this.dirtyTracker = new DirtyTracker();

    // Mark everything dirty initially
    this.dirtyTracker.markMultipleDirty([
      DirtyFlag.DATA,
      DirtyFlag.THEME,
      DirtyFlag.VIEWPORT,
    ]);
  }

  // ==================================================================================
  // PUBLIC API
  // ==================================================================================

  /**
   * Main render method
   */
  public render(
    ctx: CanvasRenderingContext2D,
    places: Map<string, Place>,
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    selectedPlaceIds: Set<string>,
    activeLevels: Set<number>,
    activeRows: Set<string>,
    canvasSize?: { width: number; height: number }
  ): void {
    const renderStart = performance.now();

    // Build render context
    const context: RenderContext = {
      ctx,
      places,
      viewState,
      worldToScreen,
      selectedPlaceIds,
      activeLevels,
      activeRows,
      canvasSize,
      config: this.config,

      // Inject subsystems
      styleManager: this.styleManager,
      labelRenderer: this.labelRenderer,
      layoutCache: this.layoutCache,
      spriteFactory: this.spriteFactory,
      columnBuilder: this.columnBuilder,
      columnSorter: this.columnSorter,
      culler: this.culler,
      placeDrawer: this.placeDrawer,
      dirtyTracker: this.dirtyTracker,
    };

    // Execute pipeline
    this.pipeline.execute(context);

    // Update stats
    this.updateStats(renderStart);

    // Clear dirty flags
    this.dirtyTracker.clearAll();
  }

  /**
   * Notify renderer that places data changed
   */
  public notifyDataChanged(): void {
    this.dirtyTracker.markDirty(DirtyFlag.DATA);
  }

  /**
   * Notify renderer that viewport changed (pan/zoom/rotate)
   */
  public notifyViewportChanged(): void {
    this.dirtyTracker.markDirty(DirtyFlag.VIEWPORT);
  }

  /**
   * Notify renderer that selection changed
   */
  public notifySelectionChanged(): void {
    this.dirtyTracker.markDirty(DirtyFlag.SELECTION);
  }

  /**
   * Notify renderer that theme changed
   */
  public notifyThemeChanged(theme: Theme): void {
    this.theme = theme;
    this.styleManager.updateTheme(theme);
    this.labelRenderer.updateTheme(theme);
    this.dirtyTracker.markDirty(DirtyFlag.THEME);
  }

  /**
   * Notify renderer that config changed
   */
  public notifyConfigChanged(config: Partial<PlaceRendererConfig>): void {
    Object.assign(this.config, config); // Mutate to let subsystems see changes
    this.layoutCache.clear();
    this.spriteFactory.clear();
    this.dirtyTracker.markDirty(DirtyFlag.CONFIG);
  }


  /**
   * Clear all caches
   */
  public clearCaches(): void {
    this.styleManager.clearCaches();
    this.labelRenderer.clearCache();
    this.layoutCache.clear();
    this.spriteFactory.clear();
    this.dirtyTracker.markMultipleDirty([
      DirtyFlag.DATA,
      DirtyFlag.THEME,
      DirtyFlag.VIEWPORT,
    ]);
  }

  /**
   * Get comprehensive statistics
   */
  public getStats(): {
    render: RenderStats;
    pipeline: ReturnType<RenderPipeline['getStats']>;
    styleManager: ReturnType<PlaceStyleManager['getCacheStats']>;
    labelRenderer: ReturnType<PlaceLabelRenderer['getCacheStats']>;
    layoutCache: { size: number };
    spriteFactory: { hits: number; misses: number; hitRate: number };
    columnBuilder: ReturnType<ColumnBuilder['getPoolStats']>;
    dirtyTracker: ReturnType<DirtyTracker['getStats']>;
  } {
    return {
      render: { ...this.renderStats },
      pipeline: this.pipeline.getStats(),
      styleManager: this.styleManager.getCacheStats(),
      labelRenderer: this.labelRenderer.getCacheStats(),
      layoutCache: { size: this.layoutCache.size },
      spriteFactory: {
        ...this.spriteFactory.stats,
        hitRate: this.spriteFactory.getHitRate(),
      },
      columnBuilder: this.columnBuilder.getPoolStats(),
      dirtyTracker: this.dirtyTracker.getStats(),
    };
  }

  /**
   * Log performance stats to console
   */
  public logStats(): void {
    const stats = this.getStats();
    console.group('📊 PlaceRenderer Performance Stats');
    console.log('Render:', stats.render);
    console.log('Pipeline:', stats.pipeline);
    console.log('Style Manager:', stats.styleManager);
    console.log('Label Renderer:', stats.labelRenderer);
    console.log('Layout Cache:', stats.layoutCache);
    console.log('Sprite Factory:', stats.spriteFactory);
    console.log('Column Builder:', stats.columnBuilder);
    console.log('Dirty Tracker:', stats.dirtyTracker);
    console.groupEnd();
  }

  // ==================================================================================
  // PRIVATE METHODS
  // ==================================================================================

  private updateStats(startTime: number): void {
    const frameTime = performance.now() - startTime;
    this.renderStats.frameCount++;
    this.renderStats.totalTime += frameTime;
    this.renderStats.avgTime =
      this.renderStats.totalTime / this.renderStats.frameCount;
    this.renderStats.lastFrameTime = frameTime;

    // Log slow frames
    if (frameTime > 16.67) {
      // 60 FPS threshold
      console.warn(
        `⚠️ Slow frame detected: ${frameTime.toFixed(2)}ms (target: 16.67ms)`
      );
    }
  }
}
